import type { EggLevel } from 'contexts/EggContext';

/// Unparsed data sent from the client side

type PairedBreaker = {
  tokenId: TokenId;
  imageUrl: string;
};
export interface CollectionSharedRaw {
  tokenId: TokenId;
  imageUrl: string;
  collection: CollectionEntityType;
}

export type CollectionEggRaw = CollectionSharedRaw & {
  collection: 'eggs';
  level: number;
  pairedBreakers?: PairedBreaker[];
  hatching: boolean;
};

export type CollectionBreakerRaw = CollectionSharedRaw & {
  collection: 'duskbreakers';
};

export type CollectionGribbleRaw = CollectionSharedRaw & {
  collection: 'gribbles';
  hatchStartedAt: Date;
  pairedBreakers?: PairedBreaker[];
};

export type CollectionEntityRaw = CollectionEggRaw | CollectionBreakerRaw | CollectionGribbleRaw;

type DisplayableImageUrls = {
  thumbnail: string;
  full: string;
};

/**
 * Using a different data model for eggs specific to the client, as the
 * information that's returned from the server has to be polymorphic
 * over all the NFTs that a user can own
 */
export type CollectionEgg = {
  id: number;
  level: EggLevel;
  pairedBreaker?: {
    paired: boolean;
    breakerSrc: string;
  };
  hatching?: boolean;
  // We don't know if we can retrieve the date that
  // a gribble was incubated at
  createdAt?: Date;
  image: DisplayableImageUrls;
};

export type CollectionGribble = {
  id: number;
  pairedBreaker?: {
    paired: boolean;
    breakerSrc: string;
  };
  image: DisplayableImageUrls;
  hatchStartedAt: Date;
  rendered?: boolean;
};

export type CollectionBreaker = {
  id: number;
  image: DisplayableImageUrls;
};

export const CollectionEntities = {
  Gribbles: 'gribbles',
  Eggs: 'eggs',
  Duskbreakers: 'duskbreakers',
} as const;

export type CollectionEntities = typeof CollectionEntities;

export type CollectionEntityType = CollectionEntities[keyof CollectionEntities];

/**
 * Each collection entity can be fetched as well as
 * all categories all at once
 */
export type CollectionFetchType = 'all' | CollectionEntityType;

export type CollectionEntity =
  | { type: CollectionEntities['Eggs']; item: CollectionEgg }
  | { type: CollectionEntities['Gribbles']; item: CollectionGribble }
  | { type: CollectionEntities['Duskbreakers']; item: CollectionBreaker };

export type AttributeRaw = {
  traitType: string;
  value: string;
  max_value?: string;
};

export type EntityMetadata = {
  name: string;
  description: string;
  imageUrl: string;
  external_url: string;
  attributes: AttributeRaw[];
  tokenId: TokenId;
  pairedBreakers?: PairedBreaker[];
  level?: number;
  hatchStartedAt?: string;
};

export type MetadataResponse = {
  item: EntityMetadata;
};

export type CollectionStatsRaw = {
  duskbreakers: number;
  gribbles: number;
  eggs: number;
};

export type TokenId = {
  value: number;
};

export type CollectionRaw = {
  items: CollectionEntityRaw[];
  cursor: TokenId | null;
  cursorCollection: CollectionEntityType;
  collectionTotals: CollectionStatsRaw;
};

export type EggCollectionRaw = {
  items: CollectionEggRaw[];
  cursor: TokenId | null;
  cursorCollection: CollectionEntityType;
  collectionTotals: CollectionStatsRaw;
};

export type CollectionResponse = {
  items: CollectionEntity[];
  cursor: number | null;
  cursorType: CollectionEntityType;
  cursorCollection: CollectionEntityType;
  collectionTotals: CollectionStatsRaw;
};

export type EggCollectionResponse = {
  items: CollectionEgg[];
  cursor: number | null;
  cursorType: CollectionEntityType;
  cursorCollection: CollectionEntityType;
  collectionTotals: CollectionStatsRaw;
};
